import { z } from 'zod';
import {
  emailSchema,
  isoDateSchema,
  longTextSchema,
  nonNegativeMoneySchema,
  paginationSchema,
  phoneSchema,
  positiveMoneySchema,
  shortTextSchema,
  sortOrderSchema,
  uuidSchema,
} from '../primitives.js';
import { EMPLOYMENT_STATUSES, MEMBER_ROLES, MEMBER_STATUSES, PAYMENT_METHODS } from '../enums.js';

// ── Customers ───────────────────────────────────────────────────────────────

export const createCustomerSchema = z.object({
  name: shortTextSchema,
  /**
   * Which location is adding them.
   *
   * Optional, because a customer belongs to the business rather than to one
   * counter, and somebody who sees every location may add one without naming
   * a branch. A caller confined to locations has to say — the API refuses
   * otherwise — or the customer they just created would be invisible to them.
   * See apps/api/src/modules/customers/branches.ts.
   */
  branchId: z.string().uuid().nullable().optional(),
  phone: phoneSchema.nullable().optional(),
  email: emailSchema.nullable().optional(),
  address: longTextSchema.optional(),
  notes: longTextSchema.optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
});
export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;

/*
  Editing a customer never moves them between counters: a link is added by
  serving somebody, not by typing. So `branchId` is deliberately absent here.
*/
export const updateCustomerSchema = createCustomerSchema.omit({ branchId: true }).partial();
export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>;

/**
 * Where a customer stands, from their purchases.
 *
 *   none        — on file, nothing bought yet;
 *   first_time  — one purchase;
 *   returning   — two or more;
 *   frequent    — four or more in the last 90 days;
 *   inactive    — nothing in the last 90 days (wins over the others).
 */
export const CUSTOMER_SEGMENTS = ['none', 'first_time', 'returning', 'frequent', 'inactive'] as const;
export type CustomerSegment = (typeof CUSTOMER_SEGMENTS)[number];
export const CUSTOMER_SEGMENT_LABELS: Record<CustomerSegment, string> = {
  none: 'No purchases yet',
  first_time: 'First-time',
  returning: 'Returning',
  frequent: 'Frequent',
  inactive: 'Inactive',
};

export const customerFiltersSchema = paginationSchema.extend({
  search: z.string().trim().max(120).optional(),
  tag: z.string().trim().max(40).optional(),
  /** Only customers who currently owe money. */
  owingOnly: z.coerce.boolean().optional(),
  /** Patronage segment — see CUSTOMER_SEGMENTS. */
  segment: z.enum(CUSTOMER_SEGMENTS).optional(),
  sortBy: z.enum(['name', 'createdAt', 'totalSpent', 'lastPurchaseAt', 'transactionCount']).default('name'),
  sortOrder: sortOrderSchema.default('asc'),
});
export type CustomerFilters = z.infer<typeof customerFiltersSchema>;

export const customerSchema = z.object({
  id: uuidSchema,
  businessId: uuidSchema,
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  notes: z.string().nullable(),
  tags: z.array(z.string()),
  /**
   * Lifetime NET spending: sales that were not voided or deleted, less what
   * was refunded against them. A fully refunded sale counts for nothing.
   */
  totalSpent: z.string(),
  /** Owed now: unsettled sales plus any migrated opening balance. */
  totalOutstanding: z.string(),
  salesOutstanding: z.string(),
  openingBalanceOutstanding: z.string(),
  /** Purchases: sales with something kept after refunds. */
  transactionCount: z.number(),
  averagePurchase: z.string().nullable(),
  firstPurchaseAt: z.string().nullable(),
  lastPurchaseAt: z.string().nullable(),
  purchasesLast90Days: z.number(),
  segment: z.enum(CUSTOMER_SEGMENTS),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Customer = z.infer<typeof customerSchema>;

// ── Staff ───────────────────────────────────────────────────────────────────
//
// A staff member is a person the business employs. They may or may not have a
// login: a rider or cook is recorded here for expense attribution and payroll
// without ever getting an account. `userId` links them to a login when one is
// created, and `business_members` remains the source of truth for access.

export const createStaffSchema = z.object({
  fullName: shortTextSchema,
  phone: phoneSchema.nullable().optional(),
  email: emailSchema.nullable().optional(),
  roleTitle: shortTextSchema.optional(),
  branchId: uuidSchema.nullable().optional(),
  employmentStatus: z.enum(EMPLOYMENT_STATUSES).default('active'),
  salaryAmount: nonNegativeMoneySchema.nullable().optional(),
  hiredAt: isoDateSchema.nullable().optional(),
  notes: longTextSchema.optional(),
  /** When set, also invites the person to the dashboard with this role. */
  inviteAs: z.enum(MEMBER_ROLES).nullable().optional(),
});
export type CreateStaffInput = z.infer<typeof createStaffSchema>;

export const updateStaffSchema = createStaffSchema.omit({ inviteAs: true }).partial();
export type UpdateStaffInput = z.infer<typeof updateStaffSchema>;

export const staffFiltersSchema = paginationSchema.extend({
  search: z.string().trim().max(120).optional(),
  branchId: uuidSchema.optional(),
  employmentStatus: z.enum(EMPLOYMENT_STATUSES).optional(),
  /**
   * One job title exactly, as `GET /staff/roles` lists them.
   *
   * A free-text column, so the filter offers what the business has actually
   * typed rather than a fixed vocabulary — and matches case-insensitively,
   * because "Rider" and "rider" are one role to everybody but the database.
   */
  roleTitle: shortTextSchema.optional(),
  sortBy: z.enum(['fullName', 'hiredAt', 'createdAt']).default('fullName'),
  sortOrder: sortOrderSchema.default('asc'),
});
export type StaffFilters = z.infer<typeof staffFiltersSchema>;

export const staffSchema = z.object({
  id: uuidSchema,
  businessId: uuidSchema,
  userId: uuidSchema.nullable(),
  fullName: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  roleTitle: z.string().nullable(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  employmentStatus: z.enum(EMPLOYMENT_STATUSES),
  salaryAmount: z.string().nullable(),
  hiredAt: z.string().nullable(),
  notes: z.string().nullable(),
  /**
   * The membership that is this person's login, when there is one.
   *
   * Needed to change it: the screens that set access and permissions act on
   * `/members/:id`, and the staff record is where they are reached from.
   */
  memberId: uuidSchema.nullable(),
  /** Dashboard access, when this staff member has a login. */
  memberRole: z.enum(MEMBER_ROLES).nullable(),
  /**
   * Whether that login currently works. `suspended` is what deactivating a
   * staff member leaves behind and what reactivating undoes; the role, the
   * location assignments and any permission overrides survive both.
   */
  memberStatus: z.enum(MEMBER_STATUSES).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Staff = z.infer<typeof staffSchema>;

/**
 * A payment against what a customer owed before the business moved to CenoX
 * (an opening balance). Money in, never revenue.
 */
export const openingBalancePaymentSchema = z.object({
  amount: positiveMoneySchema,
  method: z.enum(PAYMENT_METHODS),
  /** The day the money arrived, in the business's own calendar. */
  paidOn: isoDateSchema,
  /** The cash or bank account it went into; left out, the location's default for the method. */
  accountId: uuidSchema.nullable().optional(),
  branchId: uuidSchema.nullable().optional(),
  reference: z.string().trim().max(120).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
  idempotencyKey: z.string().trim().min(8).max(120),
  /** In a pharmacy: the slip or bank alert for a non-cash repayment has been seen. */
  confirmed: z.boolean().optional(),
});
export type OpeningBalancePaymentInput = z.infer<typeof openingBalancePaymentSchema>;
